import { useEffect, useState } from "react";
import { portalApi, formatApiErrorDetail, NIVELES } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import axios from "axios";

const vacio = {
  nombre: "", apellidos: "", fecha_nacimiento: "", nivel: "naranja",
  observaciones: "", sede_id: "", gdpr_aceptado: false, gdpr_imagen: false,
};

export const AltaHijoDialog = ({ open, onOpenChange, sedeSugerida, titulo, descripcion, onCreado }) => {
  const [form, setForm] = useState(vacio);
  const [sedes, setSedes] = useState([]);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({ ...vacio, sede_id: sedeSugerida || "" });
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/publico/sedes`)
      .then(({ data }) => setSedes(data))
      .catch(() => setSedes([]));
  }, [open, sedeSugerida]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const guardar = async (confirmar = false) => {
    if (!form.nombre.trim()) return toast.error("Indica el nombre del hijo/a");
    if (!form.gdpr_aceptado) return toast.error("Debes aceptar la política de protección de datos");
    setGuardando(true);
    try {
      const { data } = await portalApi.post(
        `/portal/hijos${confirmar ? "?confirmar=true" : ""}`,
        { ...form, sede_id: form.sede_id || null });
      toast.success(`${data.nombre} añadido con matrícula ${data.matricula}`);
      onOpenChange(false);
      onCreado?.(data);
    } catch (e) {
      if (e.response?.status === 409) {
        if (window.confirm(formatApiErrorDetail(e.response.data.detail))) {
          setGuardando(false);
          return guardar(true);
        }
      } else {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
      }
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl max-h-[92vh] overflow-y-auto" data-testid="alta-hijo-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{titulo || "Añadir hijo/a"}</DialogTitle>
        </DialogHeader>
        {descripcion && <p className="text-sm text-muted-foreground -mt-2">{descripcion}</p>}
        <div className="grid gap-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label>Nombre *</Label>
              <Input value={form.nombre} onChange={(e) => set("nombre", e.target.value)} data-testid="hijo-nombre-input" />
            </div>
            <div className="grid gap-2">
              <Label>Apellidos</Label>
              <Input value={form.apellidos} onChange={(e) => set("apellidos", e.target.value)} data-testid="hijo-apellidos-input" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label>Fecha de nacimiento</Label>
              <Input type="date" value={form.fecha_nacimiento}
                onChange={(e) => set("fecha_nacimiento", e.target.value)} data-testid="hijo-fecha-input" />
            </div>
          </div>
          <div className="grid gap-2">
            <Label>Sede</Label>
            <Select value={form.sede_id} onValueChange={(v) => set("sede_id", v)}>
              <SelectTrigger data-testid="hijo-sede-select"><SelectValue placeholder="Selecciona sede" /></SelectTrigger>
              <SelectContent>
                {sedes.map((s) => <SelectItem key={s.id} value={s.id}>{s.nombre}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label>Observaciones</Label>
            <Textarea value={form.observaciones} onChange={(e) => set("observaciones", e.target.value)}
              placeholder="Algo que debamos saber (opcional)" data-testid="hijo-observaciones-input" />
          </div>
          <div className="flex items-center justify-between rounded-lg border border-border p-3">
            <Label>Acepto la política de protección de datos *</Label>
            <Switch checked={form.gdpr_aceptado} onCheckedChange={(v) => set("gdpr_aceptado", v)} data-testid="hijo-gdpr-switch" />
          </div>
          <div className="flex items-center justify-between rounded-lg border border-border p-3">
            <Label>Autorizo el uso de sus imágenes en la galería</Label>
            <Switch checked={form.gdpr_imagen} onCheckedChange={(v) => set("gdpr_imagen", v)} data-testid="hijo-gdpr-imagen-switch" />
          </div>
        </div>
        <DialogFooter>
          <Button className="rounded-full font-semibold" onClick={guardar} disabled={guardando} data-testid="guardar-hijo-btn">
            {guardando ? <Loader2 className="h-4 w-4 animate-spin" /> : "Añadir hijo/a"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
